import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface User {
  id: number;
  email: string;
  name: string;
  avatar_url?: string;
  organization_id: number;
  role: string;
  subscription_tier: string;
  credits_remaining: number;
  credits_expires_at?: string | null;
  onboarding_completed?: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User, token: string) => void;
  updateUser: (updates: Partial<User>) => void;
  updateCredits: (credits: number) => void;
  syncCredits: () => Promise<void>;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  checkAuth: () => Promise<void>;
}

// BroadcastChannel for cross-tab credit synchronization
const creditChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('sweetpilot-credits')
  : null;

// BroadcastChannel for cross-tab auth synchronization (login/logout)
const authChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('sweetpilot-auth')
  : null;

// Flag to prevent re-broadcasting events received from other tabs
let isFromBroadcast = false;

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      setUser: (user, token) => {
        set({
          user,
          token,
          isAuthenticated: true,
          error: null,
        });
        // Broadcast login to other tabs
        if (!isFromBroadcast) {
          authChannel?.postMessage({ type: 'session-started', user, token });
        }
      },

      updateUser: (updates) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      updateCredits: (credits: number) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, credits_remaining: credits } });
          // Broadcast to other tabs
          creditChannel?.postMessage({ type: 'credits-update', credits });
        }
      },

      syncCredits: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await fetch(`${API_URL}/user/credits`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.ok) {
            const data = (await response.json()) as {
              success: boolean;
              data?: {
                credits_remaining: number;
                credits_expires_at: string | null;
              }
            };
            if (data.success && data.data) {
              const { user } = get();
              if (user) {
                set({
                  user: {
                    ...user,
                    credits_remaining: data.data.credits_remaining,
                    credits_expires_at: data.data.credits_expires_at,
                  }
                });
              }
            }
          }
        } catch (error) {
          console.error('Failed to sync credits:', error);
        }
      },

      logout: () => {
        // Broadcast logout to other tabs before clearing state
        if (!isFromBroadcast) {
          authChannel?.postMessage({ type: 'session-ended' });
        }
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
        // Clear persisted storage
        sessionStorage.removeItem('flowai-auth');
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      checkAuth: async () => {
        const { token } = get();

        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await fetch(`${API_URL}/user/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = (await response.json()) as { success: boolean; data?: User };
            if (data.success && data.data) {
              set({
                user: data.data,
                isAuthenticated: true,
                isLoading: false,
              });
              return;
            }
          }

          // Token invalid or expired
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch {
          set({
            isLoading: false,
            isAuthenticated: false,
          });
        }
      },
    }),
    {
      name: 'flowai-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
    }
  )
);

// API Response types
interface APIResponse {
  success: boolean;
  data?: { user: User; token: string };
  error?: string;
}

// Auth API functions
export async function login(email: string, password: string): Promise<APIResponse> {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  const data = (await response.json()) as APIResponse;

  if (!response.ok) {
    throw new Error(data.error || 'Login failed');
  }

  return data;
}

export async function register(email: string, password: string, name: string): Promise<APIResponse> {
  const response = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, name }),
  });

  const data = (await response.json()) as APIResponse;

  if (!response.ok) {
    throw new Error(data.error || 'Registration failed');
  }

  return data;
}

export async function forgotPassword(email: string): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = (await response.json()) as { success: boolean; message?: string; error?: string };

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send reset email');
  }

  return data;
}

// Standalone function to update credits (for use outside of React components)
export function updateCredits(credits: number): void {
  const { user } = useAuthStore.getState();
  if (user) {
    useAuthStore.setState({
      user: { ...user, credits_remaining: credits }
    });
    // Broadcast to other tabs
    creditChannel?.postMessage({ type: 'credits-update', credits });
  }
}

// Listen for credit updates from other tabs
if (creditChannel) {
  creditChannel.onmessage = (event) => {
    if (event.data?.type === 'credits-update') {
      const { user } = useAuthStore.getState();
      if (user) {
        useAuthStore.setState({
          user: { ...user, credits_remaining: event.data.credits }
        });
      }
    }
  };
}

// Listen for auth events (login/logout) from other tabs
if (authChannel) {
  authChannel.onmessage = (event) => {
    isFromBroadcast = true;
    try {
      if (event.data?.type === 'session-ended') {
        // Save current page path before logout so we can restore it on re-login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath.startsWith('/dashboard')) {
          sessionStorage.setItem('sweetpilot-pre-logout-path', currentPath);
        }
        useAuthStore.getState().logout();
      } else if (event.data?.type === 'session-started') {
        const { user, token } = event.data;
        if (user && token) {
          useAuthStore.getState().setUser(user, token);
        }
      }
    } finally {
      isFromBroadcast = false;
    }
  };
}

// Clean up legacy localStorage auth (now using per-tab sessionStorage)
if (typeof window !== 'undefined' && localStorage.getItem('flowai-auth')) {
  localStorage.removeItem('flowai-auth');
}

// Sync credits when window gains focus (user switches back to this tab)
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const { isAuthenticated, syncCredits } = useAuthStore.getState();
      if (isAuthenticated) {
        syncCredits();
      }
    }
  });

  // Also sync on window focus (covers more edge cases)
  window.addEventListener('focus', () => {
    const { isAuthenticated, syncCredits } = useAuthStore.getState();
    if (isAuthenticated) {
      syncCredits();
    }
  });
}
